/**
 * Security events management for the admin panel
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import { SecurityEvent, SecurityEventFilters } from '../../types/admin';
import { formatDate } from '../../utils/formatters';

const severityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'default';
  }
};

const SecurityManagement: React.FC = () => {
  const {
    securityEvents,
    securityEventsLoading,
    securityEventsError,
    loadSecurityEvents,
    resolveSecurityEvent,
    createSecurityIncident,
    clearError,
  } = useAdmin();

  const [filters, setFilters] = useState<SecurityEventFilters>({
    page: 1,
    page_size: 50,
  });
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveOpen, setResolveOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [incidentForm, setIncidentForm] = useState({
    event_type: 'suspicious_activity',
    severity: 'medium' as SecurityEvent['severity'],
    description: '',
    ip_address: '',
  });

  useEffect(() => {
    loadSecurityEvents(filters);
  }, [loadSecurityEvents, filters]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadSecurityEvents(filters);
    } finally {
      setRefreshing(false);
    }
  };

  const openResolve = (event: SecurityEvent) => {
    setSelectedEvent(event);
    setResolveNotes('');
    setResolveOpen(true);
  };

  const handleResolve = async () => {
    if (!selectedEvent) return;
    await resolveSecurityEvent(selectedEvent.id, resolveNotes || undefined);
    setResolveOpen(false);
    setSelectedEvent(null);
  };

  const handleCreateIncident = async () => {
    if (!incidentForm.description.trim()) return;
    await createSecurityIncident({
      event_type: incidentForm.event_type,
      severity: incidentForm.severity,
      description: incidentForm.description.trim(),
      ip_address: incidentForm.ip_address.trim() || undefined,
    });
    setCreateOpen(false);
    setIncidentForm({
      event_type: 'suspicious_activity',
      severity: 'medium',
      description: '',
      ip_address: '',
    });
  };

  if (securityEventsLoading && securityEvents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" display="flex" alignItems="center" gap={1}>
          <SecurityIcon />
          Security
        </Typography>
        <Box display="flex" gap={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Log Incident
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {securityEventsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {securityEventsError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity</InputLabel>
                <Select
                  value={filters.severity ?? ''}
                  label="Severity"
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      severity: (e.target.value || undefined) as SecurityEventFilters['severity'],
                      page: 1,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.resolved === undefined ? '' : filters.resolved ? 'resolved' : 'open'
                  }
                  label="Status"
                  onChange={(e) => {
                    const v = e.target.value;
                    setFilters((f) => ({
                      ...f,
                      resolved: v === '' ? undefined : v === 'resolved',
                      page: 1,
                    }));
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Severity</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {securityEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No security events found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              securityEvents.map((event) => (
                <TableRow key={String(event.id)} hover>
                  <TableCell>
                    <Chip
                      label={event.severity.toUpperCase()}
                      color={severityColor(event.severity) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {event.event_type.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={event.description}>
                      {event.description}
                    </Typography>
                  </TableCell>
                  <TableCell>{event.ip_address || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={event.resolved ? 'Resolved' : 'Open'}
                      color={event.resolved ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(event.created_at)}</TableCell>
                  <TableCell align="right">
                    {!event.resolved && (
                      <Button size="small" onClick={() => openResolve(event)}>
                        Resolve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Security Event</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box pt={1}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {selectedEvent.description}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Resolution notes (optional)"
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={handleResolve}>
            Mark Resolved
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Security Incident</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Event type</InputLabel>
                <Select
                  value={incidentForm.event_type}
                  label="Event type"
                  onChange={(e) =>
                    setIncidentForm((f) => ({ ...f, event_type: e.target.value }))
                  }
                >
                  <MenuItem value="failed_login">Failed login</MenuItem>
                  <MenuItem value="suspicious_activity">Suspicious activity</MenuItem>
                  <MenuItem value="data_breach">Data breach</MenuItem>
                  <MenuItem value="policy_violation">Policy violation</MenuItem>
                  <MenuItem value="incident">Other incident</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity</InputLabel>
                <Select
                  value={incidentForm.severity}
                  label="Severity"
                  onChange={(e) =>
                    setIncidentForm((f) => ({
                      ...f,
                      severity: e.target.value as SecurityEvent['severity'],
                    }))
                  }
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                required
                value={incidentForm.description}
                onChange={(e) =>
                  setIncidentForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IP address (optional)"
                value={incidentForm.ip_address}
                onChange={(e) =>
                  setIncidentForm((f) => ({ ...f, ip_address: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateIncident}
            disabled={!incidentForm.description.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityManagement;
